import {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Button, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {useTranslation} from 'react-i18next';
import {EmailContentAiService} from 'crqlar-react-common/dist/services/automation/emailContentAi.service';
import {getErrorMessage} from 'crqlar-react-common/dist/utils/http.util';
import type {AvailableVariable} from '../../../config/nodeVariables';
import type {EmailPlaceholder} from '../../../config/emailPlaceholders';
import AutomationTextField from '../variables/AutomationTextField';
import EmailContentPreview, {EMAIL_CONTENT_HEIGHT} from './EmailContentPreview';
import {automationVariableToken} from '../../../config/variableReferences';

interface Props {
    content: string;
    subject: string;
    onChange: (content: string) => void;
    sources: AvailableVariable[];
    mappings: EmailPlaceholder[];
    readOnly?: boolean;
    /** Shared by source and preview; `EMAIL_CONTENT_HEIGHT` unless the dialog is enlarged. */
    height?: string;
}

export default function EmailContentEditor({content, subject, onChange, sources, mappings, readOnly, height = EMAIL_CONTENT_HEIGHT}: Props) {
    const {t} = useTranslation();
    const [view, setView] = useState<'source' | 'preview'>('source');
    const [aiOpen, setAiOpen] = useState(!content.trim());
    const [instruction, setInstruction] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [updated, setUpdated] = useState(false);
    const activeRequest = useRef<AbortController | null>(null);
    const variables = useMemo(() => [
        ...mappings.filter(mapping => mapping.key.trim()).map(mapping => ({
            token: `[[${mapping.key}]]`, description: mapping.key,
        })),
        ...sources.map(source => ({
            token: automationVariableToken({nodeName: source.nodeName ?? '', name: source.name}),
            description: source.description || source.name,
        })),
    ], [mappings, sources]);
    const snapshot = JSON.stringify({content, subject, variables});
    const latestSnapshot = useRef(snapshot);
    latestSnapshot.current = snapshot;
    // The parent callback closes over the rest of the node config (recipient, profile, etc.).
    const latestOnChange = useRef(onChange);
    latestOnChange.current = onChange;
    const locked = useRef(readOnly);
    locked.current = readOnly;
    useEffect(() => () => {activeRequest.current?.abort(); activeRequest.current = null;}, []);

    const cancel = () => {
        activeRequest.current?.abort();
        activeRequest.current = null;
        setBusy(false);
        setAiOpen(false);
        setError('');
    };

    const generate = async () => {
        if (readOnly || busy || !instruction.trim()) return;
        const controller = new AbortController();
        activeRequest.current = controller;
        setBusy(true);
        setError('');
        setUpdated(false);
        try {
            const next = await EmailContentAiService.suggest({content, subject, variables, instruction: instruction.trim()}, controller.signal);
            if (activeRequest.current !== controller || controller.signal.aborted) return;
            if (locked.current || latestSnapshot.current !== snapshot) {
                setError(t('The email changed while AI was working. Try again with the latest version.'));
                return;
            }
            latestOnChange.current(next);
            setInstruction('');
            setAiOpen(false);
            setUpdated(true);
        } catch (cause) {
            if (activeRequest.current === controller && !controller.signal.aborted) {
                setError(t('Could not write the email: {{msg}}', {msg: getErrorMessage(cause, t('Unexpected error'))}));
            }
        } finally {
            if (activeRequest.current === controller) {
                activeRequest.current = null;
                setBusy(false);
            }
        }
    };

    return <Stack gap={1}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant={'body1'} fontWeight={500} color={'text.secondary'}>{t('Email Content')}</Typography>
                {!readOnly && <Button size="small" startIcon={<AutoAwesomeIcon/>} disabled={busy}
                    aria-expanded={aiOpen} onClick={() => {setAiOpen(open => !open); setUpdated(false);}}>
                    {content.trim() ? t('Change with AI') : t('Create with AI')}
                </Button>}
            </Stack>
            <ToggleButtonGroup size={'small'} exclusive value={view} aria-label={t('Email content view')}
                               onChange={(_event, next) => { if (next) setView(next); }}
                               sx={{'& .MuiToggleButton-root': {px: 1.5, py: 0.5, textTransform: 'none'}}}>
                <ToggleButton value={'source'}>{t('Source')}</ToggleButton>
                <ToggleButton value={'preview'}>{t('Preview')}</ToggleButton>
            </ToggleButtonGroup>
        </Stack>
        {aiOpen && !readOnly && <Paper component="form" variant="outlined" sx={{p: 1.5}}
            onSubmit={event => {event.preventDefault(); void generate();}}>
            <Stack gap={1.5}>
                <TextField multiline minRows={2} maxRows={5} fullWidth autoFocus label={content.trim() ? t('Describe the change') : t('Describe the email')}
                    value={instruction} disabled={busy} inputProps={{maxLength: 4000}}
                    onChange={event => setInstruction(event.target.value)}
                    placeholder={content.trim() ? t('For example, make it shorter and more welcoming.')
                        : t('For example, thank the guest for their stay and invite them to complete our survey.')}/>
                {error && <Alert severity="error" sx={{alignItems: 'flex-start'}}>{error}</Alert>}
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>
                    {busy && <Typography role="status" variant="body2" color="text.secondary" sx={{mr: 'auto'}}>{t('Writing email…')}</Typography>}
                    <Button onClick={cancel}>{t('Cancel')}</Button>
                    <LoadingButton type="submit" variant="contained" startIcon={<AutoAwesomeIcon/>} loading={busy} disabled={!instruction.trim()}>
                        {content.trim() ? t('Apply') : t('Generate')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Paper>}
        {updated && <Typography role="status" variant="body2" color="text.secondary">{t('Email updated. You can undo this change.')}</Typography>}
        <AutomationTextField insertion={'placeholder'} size={'medium'} disabled={readOnly || busy}
            label={t('Email Content')} hideLabel value={content} onChange={value => {setUpdated(false); onChange(value);}}
            multiline htmlSource editorHeight={height} placeholder={'<p>Hello,</p>'}
            variables={sources} placeholders={mappings}
            sourcePreview={view === 'preview' ? <EmailContentPreview content={content} height={height}/> : undefined}/>
    </Stack>;
}
